import React from 'react';
import { Card, Skeleton, Stack, Typography, Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface MetricCardProps {
  title: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

/** Shared metric surface used by the operational and superadmin dashboards. */
const MetricCard: React.FC<MetricCardProps> = ({ title, value, hint, icon, color, loading = false }) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 1.75, md: 2.25 },
        minHeight: { xs: 118, md: 148 },
        borderRadius: 2.5,
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.42 : 0.2),
        background: `linear-gradient(135deg, ${alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.07)} 0%, ${theme.palette.background.paper} 72%)`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 1.5, color, bgcolor: alpha(color, 0.12) }}>
          {icon}
        </Box>
      </Stack>
      <Typography sx={{ mt: 2, fontSize: { xs: 25, md: 30 }, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1.1 }}>
        {loading ? <Skeleton width="72%" /> : value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {loading ? <Skeleton width="52%" /> : hint}
        </Typography>
      )}
    </Card>
  );
};

export default MetricCard;
